import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  ContinuousMonitor,
  HostLimits,
  Incident,
  ContinuousMonitoringPolicy,
  runContinuousMonitorNow,
  deleteContinuousMonitor,
  updateContinuousMonitoringPolicy,
} from '../api';
import { IncidentDetailModal } from './IncidentDetailModal';
import type { IncidentActionTarget } from './incidentActions';
import { CustomMonitorModal } from './CustomMonitorModal';
import { SlowQueriesDetails } from './SlowQueriesDetails';
import { IndexerDetails, isStaleIndexerReading } from './IndexerDetails';
import { RemediationPolicyModal } from './RemediationPolicyModal';
import { ConfirmDialog, EmptyState, Field, LoadingRows, StatusBadge } from './ui';
import { useQuery } from '../hooks';
import { queries } from '../queries';
import { humanize } from '../labels';
import { ActionMenu, Callout, Dialog, IconButton, Input, RowActions, Section } from './primitives';
import { actionLabel } from './operation/actionLabels';
import { AttentionFeed } from './status/AttentionFeed';
import type { FeedItem } from './status/AttentionFeed';
import { relativeTime, sinceWords } from './project/projectHealth';

interface MonitoringOverviewProps {
  projectId: string;
  onNavigateToDeployment?: (deploymentId: string) => void;
  /** Opens the workflow an incident's suggested action names. */
  onOpenIncidentAction?: (target: IncidentActionTarget) => void;
  /** A check to bring into view (opened from an incident, CO-100). */
  focusMonitorId?: string | null;
  /**
   * Why checks have not reported, in the server's words (the agent is not
   * in contact), so "not reported yet" is never left unexplained (UX-18).
   */
  notReportedReason?: string | null;
  /**
   * Where monitors are set up (the environment's Infrastructure section).
   * Offered in the empty state, so "no monitors yet" always has an action.
   */
  setupMonitorsTo?: string;
}

/**
 * checkReason is what a check that is not passing found, in the server's
 * words (CO-114): "1 check failed in a row" alone never says what is wrong.
 */
export function checkReason(m: Pick<ContinuousMonitor, 'status' | 'last_result'>): string | null {
  if (m.status === 'healthy' || m.status === 'disabled') return null;
  const message = m.last_result?.message?.trim();
  return message ? message : null;
}

/**
 * HostReadings (CO-161): CPU, memory, disk and load as aligned values, each
 * beside the limit the host check classifies it against (sent by the
 * server with the reading), so a warning is read against its limit. A
 * reading over its limit says so in words. The load sits beside the CPU
 * count it is read against, when the agent reports it (CO-167).
 */
export function HostReadings({
  metrics,
  limits,
  checkedAt,
}: {
  metrics: Record<string, unknown> | undefined;
  limits: HostLimits | undefined;
  checkedAt?: string;
}) {
  if (!metrics) return null;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const cpu = num(metrics.cpu_percent);
  const ram = num(metrics.ram_percent);
  const disk = num(metrics.disk_percent);
  const total = num(metrics.disk_total_bytes);
  const load = num(metrics.load_avg_1);
  const cpus = num(metrics.cpu_count);
  const rows: { label: string; value: string; reading: number | null; limit?: string; over?: number }[] = [];
  const over = (value: number | null, ...limitsInOrder: (number | undefined)[]) => {
    // The highest limit the value is over, so "over the 95% limit" wins over 85%.
    const crossed = limitsInOrder.filter((limit): limit is number => limit !== undefined && value !== null && value > limit);
    return crossed.length > 0 ? Math.max(...crossed) : undefined;
  };
  if (cpu !== null) rows.push({ label: 'CPU', value: `${Math.round(cpu)}%`, reading: cpu, limit: limits ? `limit ${limits.cpu_warn_percent}%` : undefined, over: over(cpu, limits?.cpu_warn_percent) });
  if (ram !== null) rows.push({ label: 'Memory', value: `${Math.round(ram)}%`, reading: ram, limit: limits ? `limit ${limits.ram_warn_percent}%` : undefined, over: over(ram, limits?.ram_warn_percent) });
  if (disk !== null)
    rows.push({
      label: 'Disk',
      value: `${Math.round(disk)}%${total && total > 0 ? ` of ${(total / 1024 ** 3).toFixed(1)} GB` : ''}`,
      reading: disk,
      limit: limits ? `warns above ${limits.disk_warn_percent}%, fails above ${limits.critical_percent}%` : undefined,
      over: over(disk, limits?.disk_warn_percent, limits?.critical_percent),
    });
  if (load !== null)
    rows.push({
      label: 'Load (1 min)',
      value: load.toFixed(2),
      reading: load,
      limit: cpus !== null && cpus > 0 ? `on ${cpus}\u00a0${cpus === 1 ? 'CPU' : 'CPUs'}` : undefined,
    });
  if (rows.length === 0) return null;
  return (
    <div className="host-readings" data-testid="monitor-readings">
      <dl className="host-readings__list">
        {rows.map((row) => (
          <div key={row.label} className={`host-readings__item${row.over !== undefined ? ' host-readings__item--over' : ''}`}>
            <dt>{row.label}</dt>
            <dd>
              <span className="tabular-nums host-readings__value">{row.value}</span>
              {row.over !== undefined ? (
                <span className="host-readings__limit">over the {row.over}% limit</span>
              ) : row.limit ? (
                <span className="host-readings__limit">{row.limit}</span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
      {checkedAt ? <span className="monitoring-check__meta">Checked {relativeTime(checkedAt)}</span> : null}
    </div>
  );
}

/** humanInterval says "every hour" rather than "every 3600s" (UX-18). */
export function humanInterval(seconds: number): string {
  if (seconds % 86400 === 0) return seconds === 86400 ? 'daily' : `every ${seconds / 86400} days`;
  if (seconds % 3600 === 0) return seconds === 3600 ? 'every hour' : `every ${seconds / 3600} hours`;
  if (seconds % 60 === 0) return seconds === 60 ? 'every minute' : `every ${seconds / 60} min`;
  return `every ${seconds} s`;
}

// Monitors grouped by what they watch. A group without monitors is not shown.
const categories = [
  {
    title: 'WordPress and WooCommerce',
    types: ['wordpress_page', 'wordpress_health', 'wp_cron', 'action_scheduler', 'wordpress_errors', 'wordpress_software'],
  },
  {
    title: 'Storefront and HTTPS',
    types: ['http', 'graphql', 'tls_certificate', 'deployment_health'],
  },
  {
    title: 'Database, cache, search and queue',
    types: ['database', 'database_slow_queries', 'redis', 'varnish', 'opensearch', 'catalog_search', 'rabbitmq', 'service'],
  },
  {
    title: 'Cron, queue consumers and indexers',
    types: ['cron', 'queue_consumer', 'magento_indexers'],
  },
  {
    title: 'Server and agent',
    types: ['host', 'agent', 'docker_engine'],
  },
  {
    title: 'Backups',
    types: ['backup_health'],
  },
  {
    title: 'Custom checks',
    types: ['file_watch', 'database_table_watch', 'process_watch', 'log'],
  },
];

/**
 * The policy limits the server applies to host checks (CO-166). Above a
 * warn limit the check warns; a disk (or its inodes) above the fail limit
 * fails it. The policy's interval, timeout, TLS and backup-age fields are
 * not applied by any check, so they are not offered here.
 */
const HOST_LIMIT_FIELDS: {
  key: 'host_cpu_threshold' | 'host_ram_threshold' | 'host_disk_warning_threshold' | 'host_disk_critical_threshold';
  label: string;
}[] = [
  { key: 'host_cpu_threshold', label: 'CPU: warn above (%)' },
  { key: 'host_ram_threshold', label: 'Memory: warn above (%)' },
  { key: 'host_disk_warning_threshold', label: 'Disk: warn above (%)' },
  { key: 'host_disk_critical_threshold', label: 'Disk: fail above (%)' },
];

const NO_MONITORS: ContinuousMonitor[] = [];
const NO_INCIDENTS: Incident[] = [];

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

export const MonitoringOverview: React.FC<MonitoringOverviewProps> = ({
  projectId,
  onNavigateToDeployment,
  onOpenIncidentAction,
  focusMonitorId,
  notReportedReason,
  setupMonitorsTo,
}) => {
  // The last answer stays on screen while it refreshes (every 15 s, and when
  // the tab comes back): a revisit shows the statuses it knew at once instead
  // of a skeleton, and a failed refresh is reported, never silently kept.
  const snapshot = useQuery(queries.monitoring(projectId));
  const monitors = snapshot.data?.monitors ?? NO_MONITORS;
  const incidents = snapshot.data?.incidents ?? NO_INCIDENTS;
  const policy = snapshot.data?.policy ?? null;
  const loaded = !snapshot.loading;
  // A check that could not be run reports on its own row, with Retry; a
  // check that could not be deleted reports in its confirmation (PR3).
  const [runErrors, setRunErrors] = useState<Record<string, string>>({});
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const readError = snapshot.error?.message ?? null;

  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  // Bring the check an incident named into view once the list is there.
  useEffect(() => {
    if (!focusMonitorId || !loaded) return;
    const row = document.getElementById(`monitor-${focusMonitorId}`);
    if (!row) return;
    row.scrollIntoView?.({ block: 'center' });
    row.focus?.();
  }, [focusMonitorId, loaded]);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState<ContinuousMonitor | null>(null);
  const [runningMonitorId, setRunningMonitorId] = useState<string | null>(null);
  // The thresholds being edited: a copy, so the 15 s refresh does not
  // overwrite what the person is typing.
  const [policyDraft, setPolicyDraft] = useState<ContinuousMonitoringPolicy | null>(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyError, setPolicyError] = useState<{ message: string; field?: string } | null>(null);
  const [isRemediationModalOpen, setIsRemediationModalOpen] = useState(false);
  const [monitorToDelete, setMonitorToDelete] = useState<ContinuousMonitor | null>(null);
  const [deletingMonitor, setDeletingMonitor] = useState(false);

  const reloadSnapshot = snapshot.reload;
  const fetchData = async () => {
    reloadSnapshot();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') reloadSnapshot();
    }, 15000);
    return () => clearInterval(interval);
  }, [reloadSnapshot]);

  const handleRunNow = async (monitorId: string) => {
    try {
      setRunningMonitorId(monitorId);
      setRunErrors((current) => {
        const { [monitorId]: _cleared, ...rest } = current;
        return rest;
      });
      await runContinuousMonitorNow(monitorId);
      await fetchData();
    } catch (err: unknown) {
      setRunErrors((current) => ({ ...current, [monitorId]: err instanceof Error && err.message ? err.message : 'No reason was given.' }));
    } finally {
      setRunningMonitorId(null);
    }
  };

  const handleDelete = async () => {
    if (!monitorToDelete) return;
    setDeletingMonitor(true);
    setDeleteError(null);
    try {
      await deleteContinuousMonitor(monitorToDelete.id);
      setMonitorToDelete(null);
      await fetchData();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error && err.message ? err.message : 'No reason was given.');
    } finally {
      setDeletingMonitor(false);
    }
  };

  // A refused limit takes focus, so the next keystroke corrects it.
  useEffect(() => {
    if (policyError?.field) document.getElementById(`policy-${policyError.field}`)?.focus();
  }, [policyError]);

  const openPolicy = () => {
    if (!policy) return;
    setPolicyError(null);
    setPolicyDraft(policy);
  };

  const savePolicy = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!policyDraft) return;
    if (policyDraft.host_disk_warning_threshold >= policyDraft.host_disk_critical_threshold) {
      setPolicyError({
        field: 'host_disk_warning_threshold',
        message: `The disk has to warn before it fails: set its warning limit below ${policyDraft.host_disk_critical_threshold}%.`,
      });
      return;
    }
    setSavingPolicy(true);
    setPolicyError(null);
    try {
      await updateContinuousMonitoringPolicy(projectId, policyDraft);
      setPolicyDraft(null);
      await fetchData();
    } catch (err) {
      setPolicyError({ message: err instanceof Error ? err.message : String(err), field: err instanceof ApiError ? err.field : undefined });
    } finally {
      setSavingPolicy(false);
    }
  };

  const openCustomModal = (monitor: ContinuousMonitor | null) => {
    setEditingMonitor(monitor);
    setIsCustomModalOpen(true);
  };

  const hasCritical = monitors.some((m) => m.status === 'critical') || incidents.length > 0;
  const hasWarning = monitors.some((m) => m.status === 'warning');
  const hasMonitors = monitors.length > 0;
  const unknownCount = monitors.filter((m) => m.status === 'unknown').length;
  // "All checks passing" is said only when no check is unknown: a check that
  // has not reported proves nothing, so the headline says how many and why
  // (PR1; UX_ARCHITECTURE rule B).
  const allUnknown = hasMonitors && unknownCount === monitors.length;
  const someUnknown = unknownCount > 0 && !allUnknown;
  const headline = !hasMonitors
    ? 'No monitors'
    : hasCritical
      ? incidents.length > 0
        ? `${plural(incidents.length, 'open incident')}`
        : 'A check is failing'
      : hasWarning
        ? 'Some checks report warnings'
        : allUnknown
          ? 'Monitors have not reported yet'
          : someUnknown
            ? `${unknownCount}/${monitors.length} checks not reported`
            : `${monitors.length}/${monitors.length} checks passing`;
  // The headline already counts them; the description then only adds why.
  const unknownIsHeadline = unknownCount > 0 && !hasCritical && !hasWarning;
  const level = !hasMonitors ? 'unknown' : hasCritical ? 'critical' : hasWarning ? 'warning' : unknownCount > 0 ? 'unknown' : 'ok';

  // The checks an open incident was raised from. They stay in their own
  // group with every reading, and are not listed a third time under
  // "Needs attention"; the incident names them instead (PR2).
  const incidentCheckIds = new Set(incidents.map((inc) => inc.source_monitor_id).filter((id): id is string => Boolean(id)));
  const monitorById = new Map(monitors.map((m) => [m.id, m]));
  const incidentOfCheck = new Map<string, Incident>();
  for (const inc of incidents) if (inc.source_monitor_id && !incidentOfCheck.has(inc.source_monitor_id)) incidentOfCheck.set(inc.source_monitor_id, inc);

  const incidentItems: FeedItem[] = incidents.map((inc) => {
    const source = inc.source_monitor_id ? monitorById.get(inc.source_monitor_id) : undefined;
    const sourceReason = source ? checkReason(source) : null;
    return {
      key: inc.id,
      severity: inc.severity === 'critical' ? 'critical' : 'warning',
      title: inc.title,
      summary: (
        <>
          <span className="monitoring-incident__meta">
            <StatusBadge kind="severity" value={inc.severity} />
            <StatusBadge kind="incident" value={inc.status} />
            {inc.root_cause_category ? <span>Category: {humanize(inc.root_cause_category)}</span> : null}
          </span>
          {inc.description ? <span className="monitoring-incident__desc">{inc.description}</span> : null}
        </>
      ),
      when: { label: 'Opened', at: inc.opened_at },
      action: { label: actionLabel.investigate, onClick: () => setSelectedIncident(inc) },
      related: source ? [{ key: source.id, title: `Check: ${source.name}`, detail: sourceReason ?? undefined }] : undefined,
    };
  });

  const worstIncident = incidentItems.some((item) => item.severity === 'critical') ? 'critical' : 'warning';

  // Configuring monitoring is secondary to reading it (CO-161).
  const headerActions = (
    <>
      <ActionMenu
        label="Configure monitoring"
        trigger="Configure"
        size="sm"
        testId="monitoring-configure"
        items={[
          { label: 'Add custom check', onClick: () => openCustomModal(null) },
          { label: 'Alert thresholds', onClick: openPolicy, disabled: !policy },
          { label: 'Fix policy', onClick: () => setIsRemediationModalOpen(true) },
        ]}
      />
      <IconButton icon="refresh" label="Refresh monitoring data" size="sm" onClick={fetchData} disabled={snapshot.loading || Boolean(snapshot.revalidating)} />
    </>
  );

  const needsAttention = (m: ContinuousMonitor) => m.enabled && (m.status === 'critical' || m.status === 'warning');
  const attentionMonitors = monitors.filter((m) => needsAttention(m) && !incidentCheckIds.has(m.id));
  const hasCustomCheck = monitors.some((m) => m.scope === 'user_created');

  // One check: a problem in full (what it found, since when, its readings);
  // a passing one on one line (CO-161).
  const monitorRow = (m: ContinuousMonitor) => {
    const custom = m.scope === 'user_created';
    const every = m.interval_seconds ? humanInterval(m.interval_seconds) : '';
    const cadence = custom ? `Custom check${every ? ` · ${every}` : ''}` : every ? every.charAt(0).toUpperCase() + every.slice(1) : 'Scheduled check';
    const failing = m.consecutive_failures > 0 && (m.status === 'critical' || m.status === 'warning');
    const focused = focusMonitorId === m.id;
    const reason = checkReason(m);
    const compact = !needsAttention(m) && m.type !== 'database_slow_queries';
    const incident = incidentOfCheck.get(m.id);
    const runError = runErrors[m.id];
    return (
      <li
        key={m.id}
        id={`monitor-${m.id}`}
        className={`monitoring-check${compact ? ' monitoring-check--compact' : ''}${focused ? ' monitoring-check--focus' : ''}`}
        tabIndex={focused ? -1 : undefined}
        data-testid={focused ? 'monitor-focused' : undefined}
      >
        <div className="monitoring-check__text">
          <span className="monitoring-check__name">{m.name}</span>
          <span className="monitoring-check__meta">
            {cadence}
            {failing
              ? ` · failing since ${sinceWords(m.last_status_changed_at)} (${m.consecutive_failures} ${m.consecutive_failures === 1 ? 'check' : 'checks'} in a row)`
              : ''}
            {!m.last_run_at && m.enabled ? ` · not run yet${notReportedReason ? `: ${notReportedReason}` : ''}` : ''}
          </span>
          {reason ? (
            <span className={`monitoring-check__reason monitoring-check__reason--${m.status}`} data-testid="monitor-reason">
              {reason}
            </span>
          ) : null}
          {incident ? (
            <span className="monitoring-check__incident" data-testid="monitor-incident">
              Open incident: {incident.title}{' '}
              <button type="button" className="link-button" onClick={() => setSelectedIncident(incident)} aria-label={`${actionLabel.investigate} the incident “${incident.title}”`}>
                {actionLabel.investigate}
              </button>
            </span>
          ) : null}
          {runError ? (
            <span className="monitoring-check__run-error" role="alert" data-testid="monitor-run-error">
              The check could not be run: {runError}{' '}
              <button type="button" className="link-button" onClick={() => handleRunNow(m.id)} disabled={runningMonitorId === m.id} aria-label={`${actionLabel.retry} ${m.name}`}>
                {actionLabel.retry}
              </button>
            </span>
          ) : null}
          {m.type === 'host' && !compact ? <HostReadings metrics={m.last_result?.metrics} limits={m.last_result?.limits} checkedAt={m.last_result?.checked_at} /> : null}
        </div>
        <div className="monitoring-check__side">
          <StatusBadge kind="check" value={m.type === 'magento_indexers' && isStaleIndexerReading(m) ? 'unknown' : m.status} />
          <RowActions>
            <button
              type="button"
              onClick={() => handleRunNow(m.id)}
              disabled={runningMonitorId === m.id}
              className="button button--secondary button--small"
              aria-label={`Check ${m.name} now`}
            >
              {runningMonitorId === m.id ? 'Checking' : 'Check now'}
            </button>
            {custom ? (
              <ActionMenu
                size="sm"
                label={`More actions for ${m.name}`}
                items={[
                  { label: 'Edit check', onClick: () => openCustomModal(m) },
                  {
                    label: 'Delete check',
                    danger: true,
                    onClick: () => {
                      setDeleteError(null);
                      setMonitorToDelete(m);
                    },
                  },
                ]}
              />
            ) : hasCustomCheck ? (
              <span className="monitoring-check__menu-spacer" aria-hidden="true" />
            ) : null}
          </RowActions>
        </div>
        {m.type === 'database_slow_queries' ? <SlowQueriesDetails monitor={m} /> : null}
        {m.type === 'magento_indexers' ? <IndexerDetails monitor={m} /> : null}
      </li>
    );
  };

  return (
    <div className="monitoring">
      <Section
        className="monitoring-summary"
        testId="monitoring-summary"
        title={
          loaded && hasMonitors ? (
            <>
              <span className={`level-dot level-dot--large level-dot--${level}`} aria-hidden="true" />
              Monitoring: {headline}
            </>
          ) : (
            'Monitoring'
          )
        }
        description={
          !loaded || !hasMonitors || level === 'ok'
            ? undefined
            : `${plural(monitors.length, 'monitor')}${unknownCount > 0 && !unknownIsHeadline ? `, ${unknownCount} not reported yet${notReportedReason ? ` (${notReportedReason})` : ''}` : ''}${unknownIsHeadline && notReportedReason ? ` · not reported because ${notReportedReason}` : ''}`
        }
        actions={headerActions}
      />

      {readError ? (
        <Callout
          tone="danger"
          title="Monitoring could not be read"
          testId="monitoring-read-error"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={fetchData} disabled={snapshot.loading || Boolean(snapshot.revalidating)}>
              {actionLabel.retry}
            </button>
          }
        >
          {readError}
          {hasMonitors ? ' The results below are from the last successful read.' : ''}
        </Callout>
      ) : null}

      {loaded && !hasMonitors && !readError ? (
        <EmptyState
          title="No monitors"
          description="Commerce Ops is not watching this store."
        >
          {setupMonitorsTo ? (
            <Link className="button button--primary" to={setupMonitorsTo} data-testid="monitoring-setup-link">
              Go to Infrastructure
            </Link>
          ) : null}
        </EmptyState>
      ) : null}

      {!loaded ? (
        <div className="monitoring-groups" aria-hidden="true">
          {[0, 1].map((i) => (
            <Section key={i}>
              <LoadingRows label="Loading monitors" rows={4} rowHeight="32px" />
            </Section>
          ))}
        </div>
      ) : null}

      {incidentItems.length > 0 ? (
        <Section
          tone={worstIncident}
          title={`Open incidents (${incidentItems.length})`}
          testId="monitoring-incidents"
        >
          <AttentionFeed items={incidentItems} label="Open incidents" limit={10} />
        </Section>
      ) : null}

      {loaded && attentionMonitors.length > 0 ? (
        <Section
          tone={attentionMonitors.some((m) => m.status === 'critical') ? 'critical' : 'warning'}
          title={`Needs attention (${attentionMonitors.length})`}
          testId="monitoring-attention"
        >
          <ul className="monitoring-checks">{attentionMonitors.map(monitorRow)}</ul>
        </Section>
      ) : null}

      {loaded && hasMonitors ? (
        <div className="monitoring-groups">
          {categories.map((cat) => {
            const catMonitors = monitors.filter((m) => cat.types.includes(m.type));
            const shown = catMonitors.filter((m) => !attentionMonitors.includes(m));
            if (shown.length === 0) return null;
            const passing = catMonitors.filter((m) => m.status === 'healthy').length;
            const elsewhere = catMonitors.length - shown.length;
            return (
              <Section
                key={cat.title}
                title={cat.title}
                description={`${passing}/${catMonitors.length} passing${elsewhere > 0 ? ` · ${elsewhere} under Needs attention` : ''}`}
              >
                <ul className="monitoring-checks">{shown.map(monitorRow)}</ul>
              </Section>
            );
          })}
        </div>
      ) : null}

      <ConfirmDialog
        open={monitorToDelete !== null}
        title={monitorToDelete ? `Delete the check “${monitorToDelete.name}”?` : 'Delete check?'}
        description="Commerce Ops stops running this check and its history is removed. Open incidents it raised stay until resolved. You can add the check again later."
        confirmLabel="Delete check"
        danger
        busy={deletingMonitor}
        onConfirm={handleDelete}
        onCancel={() => {
          setMonitorToDelete(null);
          setDeleteError(null);
        }}
      >
        {deleteError ? (
          <Callout tone="danger" title="The check was not deleted" testId="monitor-delete-error">
            {deleteError} Nothing was removed.
          </Callout>
        ) : null}
      </ConfirmDialog>

      {selectedIncident && (
        <IncidentDetailModal
          incident={selectedIncident}
          isOpen={!!selectedIncident}
          onClose={() => setSelectedIncident(null)}
          onRefresh={fetchData}
          onNavigateToDeployment={onNavigateToDeployment}
          onOpenAction={onOpenIncidentAction}
          monitors={monitors}
        />
      )}

      <CustomMonitorModal
        projectId={projectId}
        monitor={editingMonitor}
        isOpen={isCustomModalOpen}
        onClose={() => {
          setIsCustomModalOpen(false);
          setEditingMonitor(null);
        }}
        onSaved={fetchData}
      />

      <Dialog
        open={policyDraft !== null}
        size="sm"
        title="Alert thresholds"
        description="The host resource checks on this project's servers warn above these limits; the disk fail limit also covers inodes. Changes apply from each check's next run."
        onClose={() => setPolicyDraft(null)}
        busy={savingPolicy}
        onSubmit={savePolicy}
        testId="monitoring-policy-dialog"
        footer={
          <>
            <button type="button" className="button button--secondary" onClick={() => setPolicyDraft(null)} disabled={savingPolicy}>
              Cancel
            </button>
            <button type="submit" className="button button--primary" disabled={savingPolicy}>
              {savingPolicy ? 'Saving' : 'Save thresholds'}
            </button>
          </>
        }
      >
        {policyError ? (
          <div id="policy-error">
            <Callout tone="danger" title="The thresholds were not saved">
              {policyError.message}
            </Callout>
          </div>
        ) : null}
        {policyDraft ? (
          <div className="form-grid form-grid--2">
            {HOST_LIMIT_FIELDS.map(({ key, label }) => (
              <Field key={key} label={label}>
                <Input
                  id={`policy-${key}`}
                  name={key}
                  data-autofocus={key === 'host_cpu_threshold' ? true : undefined}
                  type="number"
                  min="10"
                  max="100"
                  required
                  autoComplete="off"
                  value={policyDraft[key]}
                  aria-invalid={policyError?.field === key ? true : undefined}
                  aria-describedby={policyError?.field === key ? 'policy-error' : undefined}
                  onChange={(e) => setPolicyDraft({ ...policyDraft, [key]: Number(e.target.value) })}
                />
              </Field>
            ))}
          </div>
        ) : null}
      </Dialog>

      <RemediationPolicyModal
        projectId={projectId}
        isOpen={isRemediationModalOpen}
        onClose={() => setIsRemediationModalOpen(false)}
      />
    </div>
  );
};
